// src/app/core/services/notificaciones.service.ts

import { Injectable, inject } from "@angular/core";
import { HttpClient, HttpParams } from "@angular/common/http";
import { BehaviorSubject, Observable, firstValueFrom } from "rxjs";

import { environment } from "../../../environments/environment";
import { AuthService } from "./auth.service";
import type {
  Notificacion,
  RespuestaPaginadaNotificaciones,
} from "./notificaciones.model";

@Injectable({ providedIn: "root" })
export class NotificacionesService {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private baseUrl = environment.baseUrl;
  private endpoint = "notifications";

  private _unreadCount = new BehaviorSubject<number>(0);
  readonly unreadCount$ = this._unreadCount.asObservable();

  private pollHandle: any = null;

  // =========================
  // GET /notifications?page&limit
  // =========================
  list(page = 1, limit = 20): Observable<RespuestaPaginadaNotificaciones> {
    const params = new HttpParams()
      .set("page", String(page))
      .set("limit", String(limit));

    return this.http.get<RespuestaPaginadaNotificaciones>(
      `${this.baseUrl}/${this.endpoint}`,
      { params },
    );
  }

  // =========================
  // GET /notifications/unread-count
  // =========================
  unreadCountFromApi(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(
      `${this.baseUrl}/${this.endpoint}/unread-count`,
    );
  }

  // =========================
  // PATCH /notifications/:id/read
  // =========================
  markRead(id: number): Observable<Notificacion> {
    return this.http.patch<Notificacion>(
      `${this.baseUrl}/${this.endpoint}/${id}/read`,
      {},
    );
  }

  // =========================
  // PATCH /notifications/read-all
  // =========================
  markAllRead(): Observable<{ ok: true }> {
    return this.http.patch<{ ok: true }>(
      `${this.baseUrl}/${this.endpoint}/read-all`,
      {},
    );
  }

  // =========================
  // Contador en vivo (campanita)
  // =========================
  async refreshUnreadCount(): Promise<void> {
    if (!this.auth.isLoggedIn()) return;
    try {
      const res = await firstValueFrom(this.unreadCountFromApi());
      this._unreadCount.next(res?.count ?? 0);
    } catch {
      /* silent – la campanita no debe romper la topbar si falla la API */
    }
  }

  setUnreadCount(n: number): void {
    this._unreadCount.next(Math.max(0, n));
  }

  decrementUnread(by = 1): void {
    this._unreadCount.next(Math.max(0, this._unreadCount.value - by));
  }

  reset(): void {
    this._unreadCount.next(0);
    this.stopPolling();
  }

  /** Refresca el contador cada `intervalMs` (por defecto 30s) mientras haya sesión. */
  startPolling(intervalMs = 30000): void {
    this.stopPolling();
    this.refreshUnreadCount();
    this.pollHandle = setInterval(() => this.refreshUnreadCount(), intervalMs);
  }

  stopPolling(): void {
    if (this.pollHandle) {
      clearInterval(this.pollHandle);
      this.pollHandle = null;
    }
  }
}
